/**
 * ZO Bull Algo Trading Platform
 * Version: 6.2.0
 */
/**
 * Support Widget Component
 * 
 * A Tawk.to-style floating support widget providing:
 * - AI Copilot chat integration
 * - Online status indicator
 * - Quick action buttons
 * - Multi-language support
 * - Responsive design
 */

import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  X, 
  Minimize2, 
  Maximize2,
  Bot,
  Headphones,
  HelpCircle,
  ChevronUp,
  Send,
  Loader2,
  Wifi,
  WifiOff,
  Moon,
  Sun,
} from 'lucide-react';
import CopilotChat from './CopilotChat';
import './SupportWidget.css';

type WidgetStatus = 'online' | 'offline' | 'busy';
type WidgetTheme = 'light' | 'dark';

interface SupportWidgetProps {
  position?: 'bottom-right' | 'bottom-left';
}

const SupportWidget: React.FC<SupportWidgetProps> = ({ 
  position = 'bottom-right' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [status, setStatus] = useState<WidgetStatus>('online');
  const [unreadCount, setUnreadCount] = useState(0);
  const [theme, setTheme] = useState<WidgetTheme>('dark');
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simulate connection status check
  useEffect(() => {
    const checkConnection = () => {
      setIsConnected(navigator.onLine);
    };
    
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);
    checkConnection();
    
    return () => {
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, []);

  // Toggle widget
  const toggleWidget = () => {
    if (isOpen && !isMinimized) {
      setIsMinimized(true);
    } else if (isOpen && isMinimized) {
      setIsOpen(false);
      setIsMinimized(false);
    } else {
      setIsOpen(true);
      setIsMinimized(false);
      setUnreadCount(0);
    }
    setShowQuickActions(false);
  };

  // Expand from minimized
  const expandWidget = () => {
    setIsMinimized(false);
  };

  // Toggle quick actions
  const toggleQuickActions = () => {
    setShowQuickActions(!showQuickActions);
  };

  // Toggle theme
  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Get status color
  const getStatusColor = () => {
    switch (status) {
      case 'online': return 'var(--success)';
      case 'offline': return 'var(--danger)';
      case 'busy': return 'var(--warning)';
      default: return 'var(--success)';
    }
  };

  // Get status text
  const getStatusText = () => {
    if (!isConnected) return 'Disconnected';
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'busy': return 'Busy';
      default: return 'Online';
    }
  };

  // Quick action buttons
  const quickActions = [
    { icon: HelpCircle, label: 'FAQ', action: () => {} },
    { icon: Headphones, label: 'Contact', action: () => {} },
    { icon: Bot, label: 'AI Chat', action: () => {} },
  ];

  return (
    <div className={`support-widget ${position} ${theme}`}>
      {/* Quick Actions Panel */}
      {showQuickActions && (
        <div className="quick-actions-panel">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className="quick-action-btn"
              onClick={action.action}
              title={action.label}
            >
              <action.icon size={20} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Chat Widget */}
      {isOpen && !isMinimized && (
        <div className="widget-container">
          {/* Header */}
          <div className="widget-header">
            <div className="widget-header-left">
              <div className="widget-avatar">
                <Bot size={24} />
              </div>
              <div className="widget-header-info">
                <h4>ZO Bull Support</h4>
                <div className="widget-status">
                  <span 
                    className={`status-dot ${status}`}
                    style={{ backgroundColor: getStatusColor() }}
                  />
                  <span>{getStatusText()}</span>
                  {!isConnected && (
                    <WifiOff size={12} className="connection-icon" />
                  )}
                </div>
              </div>
            </div>
            <div className="widget-header-actions">
              <button 
                className="widget-action-btn"
                onClick={toggleTheme}
                title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button 
                className="widget-action-btn"
                onClick={toggleQuickActions}
                title="Quick Actions"
              >
                <ChevronUp size={18} />
              </button>
              <button 
                className="widget-action-btn"
                onClick={() => setIsMinimized(true)}
                title="Minimize"
              >
                <Minimize2 size={18} />
              </button>
              <button 
                className="widget-action-btn close"
                onClick={() => { setIsOpen(false); setShowQuickActions(false); }}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="widget-content">
            <CopilotChat />
          </div>

          {/* Footer */}
          <div className="widget-footer">
            <div className="widget-footer-info">
              <MessageCircle size={14} />
              <span>Powered by AI Copilot</span>
            </div>
            <a 
              href="/copilot" 
              className="widget-footer-link"
              target="_blank"
            >
              Open Full Chat
            </a>
          </div>
        </div>
      )}

      {/* Minimized Widget */}
      {isOpen && isMinimized && (
        <div className="widget-minimized" onClick={expandWidget}>
          <div className="widget-minimized-content">
            <Bot size={20} />
            <span>Support Chat</span>
          </div>
          <button 
            className="widget-minimized-close"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Floating Action Button */}
      {!isOpen && (
        <button 
          className={`floating-btn ${isConnected ? '' : 'disconnected'}`}
          onClick={toggleWidget}
          aria-label="Open Support Chat"
        >
          <div className="floating-btn-icon">
            {isConnected ? (
              <MessageCircle size={28} />
            ) : (
              <WifiOff size={28} />
            )}
          </div>
          
          {/* Notification Badge */}
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          
          {/* Status Indicator */}
          <span 
            className="status-indicator"
            style={{ backgroundColor: getStatusColor() }}
          />
          
          {/* Pulse Animation */}
          <span className="pulse-ring" />
        </button>
      )}
    </div>
  );
};

export default SupportWidget;
